import { Box, Circle, HStack, Text } from "@chakra-ui/react";
import React from "react";
// import getYouTubeID from "get-youtube-id";
import { observer } from "mobx-react-lite";
import { ISubjectStore, ITutorSubject } from "../stores";
import { SUBJECT_EDIT_STEPS } from "../stores/subject";
import { AdditionalStudents } from "./forms/AdditionalStudents";
import CertificationsForm from "./forms/CertificationsForm";
import { SetHourlyRate } from "./forms/SetHourlyRate";
import SubjectDescriptionForm from "./forms/SubjectDescriptionForm";
import SubjectTitleForm from "./forms/SubjectTitleForm";
import RequirementsForm from "./forms/TeachingRequirements";
import SubjectPhotos from "./SubjectPhotos";
import TeachingPreference from "./TeachingPreference";
import { CheckCircleIcon } from "@chakra-ui/icons";
import {
  OverlayWrapper,
  useOverlayDisclosure,
} from "../components/OverlayRouter";
import { CustomPopupDisplay } from "./forms/SharedComponents";
import { useHistory } from "react-router";

const SubjectEditForm: React.FC<{
  currentForm: string;
  onBackClick: () => void;
  store: ITutorSubject;
  subjectStore: ISubjectStore;
  priceSuggestion: { minimum: number; maximum: number; recommended: number };
  onSubmit: (params: any, bool?: boolean) => Promise<any>;
  onDeletePhoto: (params: any) => Promise<any>;
  children?: React.ReactNode;
  educationAndWorkHistoryData: {
    education: any[];
    workHistory: any[];
  };
}> = observer(
  ({
    educationAndWorkHistoryData,
    currentForm,
    onBackClick,
    onSubmit,
    onDeletePhoto,
    subjectStore,
    priceSuggestion,
    store,
    children,
  }) => {
    function getFormRender() {
      window.scrollTo(0, 0);
      if (currentForm === SUBJECT_EDIT_STEPS.PREFERENCES) {
        return (
          <TeachingPreference
            fields={store.preferenceQuestions(subjectStore)}
            onSubmit={onSubmit}
            loading={store.loading}
            values={store.defaultPreferenceValues}
            updatePreferences={store.updatePreference}
            uploadStore={store.uploadStore}
            onBackClick={onBackClick}
          />
        );
      }
      if (currentForm === SUBJECT_EDIT_STEPS.HEADING) {
        let tuteriaInstance: any = subjectStore.getTuteriaSubject(store);
        return (
          <SubjectTitleForm
            subject={{
              name: store.name,
              subcategory: tuteriaInstance?.subcategory || "",
              category: tuteriaInstance?.category || "",
            }}
            TUTORDATA={educationAndWorkHistoryData}
            value={store.fieldValue("title")}
            loading={store.loading}
            onSubmit={onSubmit}
            onBackClick={onBackClick}
          />
        );
      }

      if (currentForm === SUBJECT_EDIT_STEPS.DESCRIPTIONS) {
        let tuteriaInstance: any = subjectStore.getTuteriaSubject(store);
        return (
          <SubjectDescriptionForm
            loading={store.loading}
            value={store.subjectDescription()}
            subject={store.name}
            titleSubject={{
              name: store.name,
              subcategory: tuteriaInstance?.subcategory || "",
              category: tuteriaInstance?.category || "",
            }}
            TUTORDATA={educationAndWorkHistoryData}
            availableSubjects={subjectStore.availableSubjects(store.id)}
            onSubmit={onSubmit}
            onBackClick={onBackClick}
            saveOnBlur={store.saveOnBlur}
            preferenceFields={store.preferenceQuestions(subjectStore)}
            uploadStore={store.uploadStore}
          />
        );
      }

      if (currentForm === SUBJECT_EDIT_STEPS.CERTIFICATIONS) {
        return (
          <CertificationsForm
            subject={store.name}
            initialValues={store.fieldValue("certifications")}
            loading={store.loading}
            onSubmit={onSubmit}
            onBackClick={onBackClick}
          />
        );
      }

      if (currentForm === SUBJECT_EDIT_STEPS.IMAGES) {
        return (
          <SubjectPhotos
            onSubmit={onSubmit}
            subject={store.name}
            onDelete={onDeletePhoto}
            onBackClick={onBackClick}
            initialPhotos={store.photos}
            loading={store.loading}
          />
        );
      }

      if (currentForm === SUBJECT_EDIT_STEPS.PRICING) {
        return (
          <SetHourlyRate
            subject={store.name}
            currency={subjectStore.pricingData.currency}
            withholdingTax={subjectStore.pricingData.withholdingTax}
            updatePriceAgreement={store.updatePriceAgreement}
            priceAgreement={subjectStore.priceAgreement}
            earningPercentage={subjectStore.earningPercentage}
            priceSuggestion={priceSuggestion}
            initialValues={store.fieldValue("hourlyRate")}
            isLoading={store.loading}
            onSavePrice={(v) => onSubmit(v, false)}
            onSubmit={onSubmit}
            onBackClick={onBackClick}
          />
        );
      }
      if (currentForm === SUBJECT_EDIT_STEPS.ADDITIONAL_STUDENT_PRICING) {
        return (
          <AdditionalStudents
            subject={store.name}
            currency={subjectStore.pricingData.currency}
            earningPercentage={subjectStore.earningPercentage}
            hourlyRate={store.fieldValue("hourlyRate")?.hourlyRate}
            initialValues={store.fieldValue("extraStudents")}
            isLoading={store.loading}
            onSavePrice={(v) => onSubmit(v, false)}
            onSubmit={onSubmit}
            onBackClick={onBackClick}
          />
        );
      }

      if (currentForm === SUBJECT_EDIT_STEPS.REQUIREMENTS) {
        return (
          <RequirementsForm
            subject={store.name}
            initialValues={store.fieldValue("teachingRequirements")}
            isLoading={store.loading}
            onSubmit={onSubmit}
            onBackClick={onBackClick}
          />
        );
      }

      return null;
    }

    return (
      <Box>
        {getFormRender()}
        {children}
      </Box>
    );
  },
);

export const StepIndicator = ({
  stepName,
  stepNumber,
  isCompleted,
  ...rest
}: {
  stepName: string;
  stepNumber: number;
  isCompleted?: boolean;
}) => {
  return (
    <HStack {...rest}>
      {isCompleted ? (
        <CheckCircleIcon fontSize="2xl" color="blue.600" />
      ) : (
        <Circle
          justifyContent="center"
          fontWeight="semibold"
          bg="gray.700"
          color="white"
        >
          {stepNumber}
        </Circle>
      )}

      <Text fontSize="xl" fontWeight="bold">
        {stepName}
      </Text>
    </HStack>
  );
};

export const HideAndShowWrapper: React.FC<{
  render: ({ onBackClick }: any) => any;
  onSubjectEdit?: any;
  setCurrentForm?: any;
  children: any;
}> = ({ render, children, setCurrentForm, onSubjectEdit }) => {
  const [displaySidebar, setDisplaySidebar] = React.useState(false);
  function onMenuClick(form: string) {
    setCurrentForm(form);
    setDisplaySidebar(true);
    if (onSubjectEdit) {
      onSubjectEdit(form);
    }
  }
  function onBackClick() {
    setDisplaySidebar(false);
    setCurrentForm("");
    if (onSubjectEdit) {
      onSubjectEdit("");
    }
  }
  return (
    <>
      {displaySidebar ? (
        <Box>{render({ onBackClick })}</Box>
      ) : (
        children({ onMenuClick, onBackClick })
      )}
    </>
  );
};

export const ModalWrapper: React.FC<{
  render: ({ onBackClick }: any) => any;
  onSubjectEdit?: any;
  setCurrentForm: any;
  children: any;
  path?: string;
}> = ({
  render,
  children,
  setCurrentForm,
  onSubjectEdit,
  path = "/subject-edit",
}) => {
  const {
    isOpen,
    rawClose: onClose,
    rawOpen: onOpen,
  } = useOverlayDisclosure(path);
  function onMenuClick(form: string) {
    setCurrentForm(form);
    if (onSubjectEdit) {
      onSubjectEdit(form);
    }
    onOpen();
  }
  function onBackClick() {
    setCurrentForm("");
    if (onSubjectEdit) {
      onSubjectEdit("");
    }
    onClose();
  }
  return (
    <>
      <CustomPopupDisplay
        path={path}
        isOpen={isOpen}
        isDesktopDrawer={true}
        onClose={() => {
          onClose();
          setCurrentForm("");
        }}
        closeOnOverlayClick={false}
      >
        {render({ onBackClick, onMenuClick })}
      </CustomPopupDisplay>
      <OverlayWrapper exact={true} path="/">
        <>{children({ onMenuClick, onBackClick })}</>
      </OverlayWrapper>
    </>
  );
};
